import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { useEntry } from '../../hooks/useEntry';
import { getEntryBySweepId, getSweepByConsumerId } from './requests';
import { SWEEP_CONSUMER_ID } from '../../config';
import { useAuth, useLoginActions } from '../../hooks/useAuth';
import { useSweep } from '../../hooks/useSweep';
import { useError } from '../../hooks/useError';
import { HomePage } from '.';
import * as Styled from './styles';
import { useFeatures } from '../../hooks/useFeatures';
import { getBettingStatus } from '../../helpers/betHelpers';
import { getPromotionConfig } from '../../components/BetPromo/utils';
import { checkIfExcludedFrom, isPick7iOSWebView } from '../../utils/authUtils';

const TermsLinkWrapper = ({ children }) => {
    return <Link to="/terms">{children}</Link>;
};

const Itv7TermsLinkWrapper = ({ children }) => {
    return (
        <Styled.ButtonLink href="https://itv7.itv.com/terms" target="_blank">
            {children}
        </Styled.ButtonLink>
    );
};

const ButtonLinkWrapper = ({ children }) => {
    return (
        <Styled.ButtonLink href="https://itv7.itv.com/home">
            {children}
        </Styled.ButtonLink>
    );
};

export const HomeContainer = ({ OnEntered }) => {
    const { sweep, setSweep } = useSweep();
    const { entry, setEntry } = useEntry();
    const { setError } = useError();
    const [isLoading, setIsLoading] = useState(true);
    const { authStore } = useAuth();
    const { openLogin } = useLoginActions();
    const { features } = useFeatures();
    const [searchParams] = useSearchParams();

    if (isPick7iOSWebView() && searchParams.has('productToken')) {
        return (
            <Navigate to={`sso/loginuser?${searchParams.toString()}`} replace />
        );
    }

    useEffect(() => {
        if (!sweep || !sweep.sweepId) {
            getSweepByConsumerId({ consumerId: SWEEP_CONSUMER_ID }).then(
                ({ data, errorMessage }) => {
                    if (errorMessage) {
                        setError(errorMessage);
                        setIsLoading(false);
                        return;
                    }

                    setSweep(data);
                    setIsLoading(false);
                }
            );
        }
    }, []);

    useEffect(() => {
        if (!authStore.loggedIn || !sweep || !sweep.sweepId) {
            if (sweep && sweep.sweepId) {
                setIsLoading(false);
                return;
            }

            return;
        }

        setIsLoading(true);

        getEntryBySweepId({
            sweepId: sweep.sweepId,
            productToken: authStore.productToken
        }).then(({ data, errorMessage }) => {
            if (errorMessage) {
                setError(errorMessage);
                setIsLoading(false);
                return;
            }

            setEntry({
                hasEntered: data.hasEntered,
                id: data.selection.id,
                name: data.selection.name,
                metaData: data.selection.metaData
            });
            setIsLoading(false);
        });
    }, [sweep, authStore.loggedIn]);

    if (entry.hasEntered && authStore.loggedIn) {
        return OnEntered;
    }

    const startDate = get(sweep, 'event.startDate', null);
    const eventStartDateTime = dayjs(startDate);
    const now = dayjs();
    const hasEventStarted = now.isAfter(eventStartDateTime);

    const isRoundOpen = get(sweep, 'status', null) === 'open';

    const sweepOpenDate = get(sweep, 'openDate', null);
    const sweepCloseDate = get(sweep, 'closeDate', null);
    const isSweepOpen =
        (now.isSame(sweepOpenDate) || now.isAfter(sweepOpenDate)) &&
        now.isBefore(sweepCloseDate);

    const canEnterSweepstake = isRoundOpen && isSweepOpen && !hasEventStarted;

    const displayCountdownTimer = get(
        features,
        'countdown_timer_enabled',
        false
    );
    const displayBettingLinks = get(features, 'betting_links_enabled', false);

    const excludeFromBetting = checkIfExcludedFrom('bet');
    const isLoggedIn = get(authStore, 'loggedIn', false);
    const regStatus = authStore.user['com.sbgcore.upgradable'];
    const promotionsConfig = getPromotionConfig({
        promotions: features.promotions,
        hasEventStarted
    });

    const bettingEnabled = getBettingStatus(
        isLoggedIn,
        displayBettingLinks,
        !excludeFromBetting
    );

    return (
        <HomePage
            isLoading={isLoading}
            sweep={sweep}
            loggedIn={authStore.loggedIn}
            openLogin={openLogin}
            TermsLink={TermsLinkWrapper}
            Itv7TermsLink={Itv7TermsLinkWrapper}
            userName={authStore.user.given_name}
            customerId={authStore.user.uki_customerid}
            ButtonLink={ButtonLinkWrapper}
            hasEventStarted={hasEventStarted}
            canEnterSweepstake={canEnterSweepstake}
            displayCountdownTimer={displayCountdownTimer}
            bettingEnabled={bettingEnabled}
            regStatus={regStatus}
            promotionsConfig={promotionsConfig}
        />
    );
};

HomeContainer.propTypes = {
    OnEntered: PropTypes.node.isRequired
};

TermsLinkWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

Itv7TermsLinkWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

ButtonLinkWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

export default HomeContainer;
